import type { FileDownloadUrl } from '../api/file-download-url.interface';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { User } from '../user/user.interface';

// ─── Kind / State ───────────────────────────────────────────────────────────

export const IMPORT_KIND = {
  HANDLING_UNITS: 'handling-units',
  ORDERS: 'orders',
} as const;
export type ImportKind = (typeof IMPORT_KIND)[keyof typeof IMPORT_KIND];

export const IMPORT_STATE = {
  ERROR: 'error',
  PARTIAL: 'partial',
  PENDING: 'pending',
  SUCCESS: 'success',
} as const;
export type ImportState = (typeof IMPORT_STATE)[keyof typeof IMPORT_STATE];

/** The set of states an import can finalize into (`error` | `partial` | `success`) — excludes `pending`. */
export type TerminalImportState = Exclude<ImportState, typeof IMPORT_STATE.PENDING>;

// ─── Line error taxonomy ────────────────────────────────────────────────────

export const IMPORT_LINE_ERROR_CODE = {
  CANCELLED_ORDER: 'cancelled_order',
  CREATE_ERROR: 'create_error',
  EMIT_ERROR: 'emit_error',
  /** Another line of the same order was invalid, so the whole order group was rejected (HU import replaces the full list, never a partial one). */
  ORDER_GROUP_ERROR: 'order_group_error',
  ORDER_NOT_FOUND: 'order_not_found',
  PARSE_ERROR: 'parse_error',
  SCOPE_ERROR: 'scope_error',
  UPDATE_ERROR: 'update_error',
  VALIDATION_ERROR: 'validation_error',
} as const;
export type ImportLineErrorCode = (typeof IMPORT_LINE_ERROR_CODE)[keyof typeof IMPORT_LINE_ERROR_CODE];

// ─── Errors ──────────────────────────────────────────────────────────────────

/**
 * A global, file-level import error pushed onto `Import.errors.global`. `code` is a plain
 * `string`, not `ImportGlobalErrorCode` — it also carries arbitrary KlareoError-style codes for
 * global validation failures (e.g. `BadRequest.EmptyImportFile`), not just `TOO_MANY_LINE_ERRORS`.
 */
export interface ImportError {
  code: string;
  details?: string;
  message: string;
}

/** A non-blocking, per-line import error. */
export interface ImportLineError {
  code: ImportLineErrorCode;
  /**
   * class-validator constraint keys (e.g. `isNotEmpty`, `whitelistValidation`) that failed for this
   * field. Set only for `validation_error` entries — absent on older documents and on other
   * error codes.
   */
  constraints?: string[];
  field?: string;
  lineIndex: number;
  message: string;
  /**
   * i18n interpolation arguments for `messageKey`, pre-stringified. Set alongside `messageKey`;
   * absent when `messageKey` is absent.
   */
  messageArgs?: Record<string, string>;
  /**
   * Stable machine key identifying `message` (for example `BadRequest.IncorrectLine`), for
   * client-side translation. Absent on older documents; clients fall back to `message`.
   */
  messageKey?: string;
  reference?: string;
  value?: string;
}

// ─── Lines count ─────────────────────────────────────────────────────────────

export interface ImportLinesCount {
  createError: number;
  created: number;
  parseError: number;
  read: number;
  skipped: number;
  /** Frozen at emission time — count of valid lines actually sent for creation. */
  submitted: number;
  total: number;
  /** Lines that matched an existing order but failed the update (rejected patch, or a cancelled-order re-import). */
  updateError: number;
  /** Lines that matched an existing order and were successfully updated instead of created (upsert). */
  updated: number;
}

// ─── Import ──────────────────────────────────────────────────────────────────

/**
 * Minimal User reference denormalized (embedded) into an `Import` document — mirrors
 * `UserComment['user']`, but `roles` is omitted (not needed for import-history display).
 */
export type ImportUser = Pick<User, 'firstName' | 'id' | 'lastName'>;

export interface Import extends BaseDocument {
  errors: {
    global: ImportError[];
    lines: ImportLineError[];
  };
  /** Original filename of the uploaded file, as submitted by the client. */
  fileName: string;
  filePath: string;
  /**
   * Deduplicated site ids resolved from EVERY candidate line of the file, regardless of per-line
   * scope authorization (the legacy "all resolved sites" semantics). Used ONLY by
   * whole-file authorization (`assertFullFileAccess`/`isFullyCoveredByScopes`) — granting a SAS
   * download, the detail view, or the error report requires covering every site the file actually
   * touches, not just the authorized-lines-only subset that `siteIds` (below) drives visibility
   * from. Optional because older documents never persisted it: `assertFullFileAccess` falls
   * back to `siteIds` (`??`, never `||`) for those legacy documents, which is semantically exact —
   * `siteIds` itself already held the all-touched-sites set on those documents — so no data
   * migration is needed.
   */
  fileSiteIds?: string[];
  /** Space-level counterpart of `fileSiteIds` — see its doc comment. */
  fileSpaceIds?: string[];
  kind: ImportKind;
  linesCount: ImportLinesCount;
  /**
   * Deduplicated site/space ids resolved from the file's AUTHORIZED candidate lines only — drives
   * `GET /import` scope-filtered visibility. Falls back to the UPLOADER's own scopes when no line
   * is authorized. Deliberately NOT used for whole-file access checks — see `fileSiteIds`.
   */
  siteIds: string[];
  spaceIds: string[];
  state: ImportState;
  user: ImportUser;
}

export type CreateImport = Omit<Import, 'id' | 'createdAt' | 'deletedAt' | 'tenantId' | 'updatedAt'>;

// ─── File download ──────────────────────────────────────────────────────────

/**
 * Signed SAS URL for downloading an import's original uploaded file — alias of the generic
 * `FileDownloadUrl` kept for back-compat with existing call sites.
 */
export type ImportFileDownloadUrl = FileDownloadUrl;

// ─── History list projection ────────────────────────────────────────────────

/**
 * Projected `GET /import` list item — omits `filePath` and narrows `errors` to just `global`;
 * per-line `errors.lines` detail can describe rows outside the caller's scope on a multi-site import.
 */
export interface ImportHistoryItem extends Omit<Import, 'errors' | 'filePath'> {
  errors: { global: ImportError[] };
}

// ─── Detail (single-import) response ────────────────────────────────────────

/**
 * `GET /import/:id` response — `Import` minus `filePath`, with `errors.lines` truncated to
 * `MAX_REPORTED_LINE_ERRORS`. `totalLineErrors` is the PRE-truncation count and cannot be derived
 * from `linesCount`: a single rejected line can yield several `ImportLineError` entries.
 */
export interface ImportDetail extends Omit<Import, 'filePath'> {
  /** True when `errors.lines` hit the persistence cap, so some rejected rows have no stored detail. */
  lineErrorsTruncated: boolean;
  totalLineErrors: number;
}
